import { useState, useEffect } from "react";
import { getUserProfileApi, updateGenderPreferenceApi } from "services/auth/authApis";
import { FiShield } from "react-icons/fi";
import { FaSpinner } from "react-icons/fa";
import toast from "react-hot-toast";

export const GenderPreferenceToggle = ({ onProfileLoaded }) => {
  const [preference, setPreference] = useState(null);
  const [gender, setGender] = useState(null);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(false);

  useEffect(() => {
    let isMounted = true;
    const loadProfile = async () => {
      try {
        setLoading(true);
        const data = await getUserProfileApi();
        if (data && isMounted) {
          setPreference(data.genderPreference);
          setGender(data.gender);
          if (onProfileLoaded) {
            onProfileLoaded(data);
          }
        }
      } catch (err) {
        console.error("Failed to load user preference:", err);
      } finally {
        if (isMounted) setLoading(false);
      }
    };

    loadProfile();
    return () => {
      isMounted = false;
    };
  }, []);

  const handleToggle = async () => {
    if (loading || updating) return;

    const previousPref = preference;
    const nextPref = preference === "SAME" ? "ANY" : "SAME";

    // Optimistic UI update
    setPreference(nextPref);
    setUpdating(true);

    try {
      await updateGenderPreferenceApi(nextPref);
      const isFemale = gender === "FEMALE";
      if (nextPref === "SAME") {
        toast.success(
          isFemale
            ? "Ride interest set to Female Only rides"
            : "Ride interest set to same gender only",
          { id: "gender-pref-update" }
        );
      } else {
        toast.success("Ride interest set to Any gender", { id: "gender-pref-update" });
      }
    } catch (error) {
      console.error("Failed to update gender preference:", error);
      // Rollback
      setPreference(previousPref);
      toast.error("Could not update travel interest. Please try again.", {
        id: "gender-pref-update",
      });
    } finally {
      setUpdating(false);
    }
  };

  const isSame = preference === "SAME";
  const isFemale = gender === "FEMALE";

  if (loading && preference === null) {
    return (
      <div className="w-full bg-zinc-50 border border-zinc-200/80 rounded-xl p-3 my-1.5 flex items-center justify-between animate-pulse">
        <div className="flex items-center gap-2.5">
          <div className="w-8 h-8 rounded-lg bg-zinc-200" />
          <div className="space-y-1.5">
            <div className="w-24 h-3 rounded bg-zinc-200" />
            <div className="w-36 h-2.5 rounded bg-zinc-200" />
          </div>
        </div>
        <div className="w-11 h-6 rounded-full bg-zinc-200" />
      </div>
    );
  }

  return (
    <div className="w-full bg-zinc-50/90 border border-zinc-200/90 rounded-xl p-3 my-1.5 transition-colors">
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-2.5">
          <div
            className={`p-2 rounded-lg transition-colors ${
              isSame
                ? isFemale
                  ? "bg-pink-100 text-pink-600"
                  : "bg-amber-100 text-amber-600"
                : "bg-zinc-200/80 text-zinc-500"
            }`}
          >
            <FiShield className="w-4 h-4" />
          </div>
          <div className="text-left">
            <span className="text-xs font-semibold text-zinc-800">
              Ride Interest
            </span>
            <p className="text-[11px] text-zinc-500 mt-0.5 leading-tight">
              {isSame
                ? isFemale
                  ? "Ride with female passengers only"
                  : "Ride with same gender only"
                : "Ride with any gender"}
            </p>
          </div>
        </div>

        <button
          type="button"
          role="switch"
          id="profile-gender-preference-toggle"
          aria-checked={isSame}
          aria-label="Toggle same gender preference"
          disabled={updating}
          onClick={handleToggle}
          className={`relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors cursor-pointer disabled:opacity-60 focus:outline-none focus:ring-2 focus:ring-amber-500 focus:ring-offset-1 ${
            isSame
              ? isFemale
                ? "bg-pink-500"
                : "bg-amber-500 hover:bg-amber-600"
              : "bg-zinc-300 hover:bg-zinc-400"
          }`}
        >
          {updating ? (
            <span
              className={`inline-flex items-center justify-center h-4 w-4 transform rounded-full bg-white shadow-sm transition-transform ${
                isSame ? "translate-x-6" : "translate-x-1"
              }`}
            >
              <FaSpinner className="w-2.5 h-2.5 text-zinc-600 animate-spin" />
            </span>
          ) : (
            <span
              className={`inline-block h-4 w-4 transform rounded-full bg-white shadow-sm transition-transform ${
                isSame ? "translate-x-6" : "translate-x-1"
              }`}
            />
          )}
        </button>
      </div>
    </div>
  );
};

export default GenderPreferenceToggle;
